import { useCallback, useMemo } from "react";
import { ScrollView, Pressable } from "react-native";
import { useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { useClips, useTimezonedBounds } from "@visioni/a3s-media/app";
import { ClipPreviewCard } from "@src/features/clips/components";
import { Loading } from "@src/components/Loading";
import { useTenantId } from "@src/features/tenants";
import type { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import { transformToMetadata } from "@src/features/clips/hooks/useDayClips";
import { useAppState } from "@src/hooks/useAppState";

interface ClipsSidePanelProps {
  cameraKey: string;
  siteId: string;
  timezone: string;
}

const MAX_VISIBLE_CLIPS = 10;

export function ClipsSidePanel({ cameraKey, siteId, timezone }: ClipsSidePanelProps) {
  const router = useRouter();
  const { theme } = useUnistyles();
  const tenantId = useTenantId();
  const appState = useAppState();

  const { startTime, endTime } = useTimezonedBounds(timezone, appState);
  const { data: clipItems, isLoading } = useClips({
    siteId: siteId as SiteId,
    assetCameraKey: cameraKey as AssetCameraKey,
    startTime: startTime,
    endTime: endTime,
    limit: MAX_VISIBLE_CLIPS,
  });

  const clips = useMemo(() => {
    if (!clipItems) return [];
    return clipItems.slice(0, MAX_VISIBLE_CLIPS).map(transformToMetadata);
  }, [clipItems]);

  const clipsBasePath = `/tenants/${encodeURIComponent(tenantId)}/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraKey)}/clips`;

  const handleClipPress = useCallback(
    (clipId: string, clipTimestamp: string) => {
      router.push(
        `${clipsBasePath}?clipId=${encodeURIComponent(clipId)}&clipTimestamp=${encodeURIComponent(clipTimestamp)}` as any,
      );
    },
    [router, clipsBasePath],
  );

  const handleSeeMore = useCallback(() => {
    router.push(clipsBasePath as any);
  }, [router, clipsBasePath]);

  return (
    <View flex={1} height="100%">
      {/* Header - Activity title with Past 24 hrs label */}
      <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="6xl">
        <Text textStyle="sm/semibold" color="text">
          Activity
        </Text>
        <Text textStyle="xs/regular" color="textMuted">
          Past 24 hrs
        </Text>
      </View>

      {/* Separator */}
      <View height={1} bg="borderCard" mb="6xl" />

      {/* Clips List */}
      {isLoading ?
        <View flex={1} alignItems="center" justifyContent="center" py="6xl">
          <Loading />
        </View>
      : clips.length === 0 ?
        <View flex={1} alignItems="center" justifyContent="center" py="6xl">
          <Text textStyle="sm/regular" color="textMuted" textAlign="center">
            No clips found
          </Text>
        </View>
      : <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            paddingBottom: theme.spacing["6xl"],
          }}
        >
          {/* Clips grid - 10px gap between cards */}
          <View gap="lg">
            {clips.map((clip) => (
              <ClipPreviewCard
                key={clip.id}
                clip={clip}
                onPress={() => handleClipPress(clip.id, clip.timestamp)}
                timezone={timezone}
              />
            ))}
          </View>

          {/* See more button - full width */}
          <View mt="6xl">
            <Pressable onPress={handleSeeMore}>
              <View
                py="xl"
                borderRadius={theme.spacing.md}
                borderWidth={1}
                borderColor="borderCard"
                bg="surfaceCard"
                alignItems="center"
              >
                <Text textStyle="sm/medium" color="text">
                  See more
                </Text>
              </View>
            </Pressable>
          </View>
        </ScrollView>
      }
    </View>
  );
}
